import ModalDialogTsoPq from '@components/ModalDialog/ModalDialogTsoPq';
import SelectTsoPq, { Option } from '@components/Select/SelectTsoPq';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { StyledMeasurementDeviceTitle } from '@pages/MeasurementVisualization/Section/MeasurementVisualizationParameters';
import { StyledSectionWrapper } from '@pages/MeasurmentExport/MeasurmentExportStyles';
import {
  useLazyGetCorrelationGetAggregatedCorrelationsCoefficientCountsQuery,
  useLazyGetCorrelationGetSingleAggregatedCorrelationsCoefficientsQuery,
} from '@redux/apiSlices/correlationApi';
import {
  useDeleteCorrelationJobByIdMutation,
  useGetCorrelationJobGetAllQuery,
} from '@redux/apiSlices/correlationJobApi';
import { useGetMeasurementDeviceGetAllQuery } from '@redux/apiSlices/measurementDeviceApi';
import { useGetMeasurementParameterGetAllQuery } from '@redux/apiSlices/measurementParameterApi';
import { useLazyGetSubstationGetSubstationDeviceHierarchyQuery } from '@redux/apiSlices/substationApi';
import { useGetTsoGetAllQuery } from '@redux/apiSlices/tsoApi';
import {
  CorrelationState,
  resetAll,
  setCalculationParameters,
  setCorrelationCounts,
  setIsLoading,
  setSelectedCalculation,
  setSingleCoefficients,
} from '@redux/slices/correlationSlice';
import { setSnackbar } from '@redux/slices/snackbarSlice';
import { TreeListItem } from '@utils/hooks/useTransfromLtreeInHyerarchy';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { StyledDeleteButton, StyledLineWrapper } from './CorellationParametersSectionStyles';
import PermissionGate from '@components/PermissionGate/PermissionGate';
import { userOrSupportRestriction } from '@components/PermissionGate/RolePermissionConfig';

const correlationStatusMap = {
  error: [3],
  waiting: [0, 1],
};

const SubmittedCorellations = () => {
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [deviceHierarchyByTso, setDeviceHierarchyByTso] = useState<{ [tsoId: number]: TreeListItem[] }>({});

  const [triggerGetHierarchy] = useLazyGetSubstationGetSubstationDeviceHierarchyQuery();
  const { data: calculations } = useGetCorrelationJobGetAllQuery(undefined, {
    pollingInterval: 15000,
  });
  const [triggerGetSingleCoefficients] = useLazyGetCorrelationGetSingleAggregatedCorrelationsCoefficientsQuery();

  const [triggerGetCounts] = useLazyGetCorrelationGetAggregatedCorrelationsCoefficientCountsQuery();
  const [deleteCorrelation] = useDeleteCorrelationJobByIdMutation();
  const { data: measurementDevices } = useGetMeasurementDeviceGetAllQuery();
  const { data: measurementParameters } = useGetMeasurementParameterGetAllQuery();
  const { data: tsos } = useGetTsoGetAllQuery();

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const selectedCalculation = useSelector(
    (state: { correlation: CorrelationState }) => state.correlation.selectedCalculation
  );

  const fetchedTsoIds = useRef<Set<number>>(new Set());
  const deleteConfirmationMessage = selectedCalculation
    ? `${t('correlation.deleteDialogContentPart1')}${selectedCalculation.label}${t('correlation.deleteDialogContentPart2')}`
    : '';

  const options = useMemo(
    () =>
      (calculations?.externalDtos ?? [])
        .map((calculation) => ({
          id: Number(calculation.id),
          value: calculation.name,
          label: calculation.name,
          disabled: correlationStatusMap.error.includes(calculation.status),
          waiting: correlationStatusMap.waiting.includes(calculation.status),
        }))
        .sort((calculationA, calculationB) =>
          calculationA.label.localeCompare(calculationB.label, undefined, { numeric: true, sensitivity: 'base' })
        ),
    [calculations?.externalDtos]
  );

  const handleCalculationChange = (option: Option | null) => {
    dispatch(setSingleCoefficients([]));
    dispatch(setCorrelationCounts([]));
    dispatch(setSelectedCalculation(option));
  };

  const handleDeleteModal = () => {
    const selectedId = selectedCalculation?.id;
    if (selectedId) setOpenModal(true);
  };

  const handleCalculationDelete = async () => {
    const selectedId = selectedCalculation?.id;
    if (!selectedId) return;

    try {
      await deleteCorrelation(selectedId).unwrap();

      dispatch(
        setSnackbar({
          open: true,
          type: 'success',
          textContent: t('correlation.snackbarDeleteSuccess'),
        })
      );
      dispatch(resetAll());
    } catch (error: any) {
      console.error(error);
      const requestID = error?.data?.traceId || undefined;
      dispatch(
        setSnackbar({
          open: true,
          type: 'error',
          textContent: t('snackbar.correlation.delete.error'),
          copyToClipboard: true,
          requestID,
        })
      );
    } finally {
      setOpenModal(false);
    }
  };

  const flattenTreeProps = (nodes: TreeListItem[], props: string[]): Record<string, any>[] => {
    if (!nodes) return [];

    return nodes.flatMap((node) => {
      const picked = props.reduce(
        (acc, prop) => {
          acc[prop] = node[prop as keyof typeof node];
          return acc;
        },
        {} as Record<string, any>
      );

      const children = node.options ? flattenTreeProps(node.options, props) : [];

      return [{ ...picked, type: node.type }, ...children];
    });
  };

  // converting measurementDevices to selectivle options:
  const mapDevicesToTreeIds = (
    hierarchyTrees: { [tsoId: number]: TreeListItem[] },
    deviceIds: number[]
  ): { [tsoId: number]: string[] } => {
    const result: { [tsoId: number]: string[] } = {};

    for (const [tsoIdStr, tree] of Object.entries(hierarchyTrees)) {
      const tsoId = Number(tsoIdStr);
      if (!tree) continue;

      const flatNodes = flattenTreeProps(tree, ['entityId', 'id', 'type']) as {
        entityId: number;
        id: string;
        type: number;
      }[];

      const mapped = flatNodes
        .filter((node) => node.type === 2 && deviceIds.includes(node.entityId))
        .map((node) => node.id);

      if (mapped.length > 0) {
        result[tsoId] = mapped;
      }
    }

    return result;
  };

  useEffect(() => {
    if (!selectedCalculation || !calculations?.externalDtos || !measurementDevices?.externalDtos) return;

    const activeCalc = calculations.externalDtos.find((c) => c.id === selectedCalculation.id);
    if (!activeCalc) return;

    const devices = [];
    for (const d of measurementDevices.externalDtos) {
      if (activeCalc.measurementDeviceIds.includes(d.id ?? -1)) {
        devices.push(d);
      }
    }

    const uniqueTsoIds = [...new Set(devices.map((d) => d.tsoId))];

    const fetchMissingHierarchies = async () => {
      for (const tsoId of uniqueTsoIds) {
        // @ts-ignore
        if (fetchedTsoIds.current.has(tsoId)) continue;

        try {
          // @ts-ignore
          const result = await triggerGetHierarchy(tsoId);
          const tree = result.data?.externalDtos ?? [];

          setDeviceHierarchyByTso((prev) => ({
            ...prev,
            // @ts-ignore
            [tsoId]: tree,
          }));

          // @ts-ignore
          fetchedTsoIds.current.add(tsoId);
        } catch (error) {
          console.error(`Error fetching hierarchy for TSO ${tsoId}:`, error);
        }
      }
    };

    fetchMissingHierarchies();
  }, [calculations?.externalDtos, measurementDevices?.externalDtos, selectedCalculation, triggerGetHierarchy]);

  useEffect(() => {
    if (!selectedCalculation || !calculations?.externalDtos || !measurementDevices?.externalDtos) return;

    const activeCalc = calculations.externalDtos.find((c) => c.id === selectedCalculation.id);
    if (!activeCalc) return;

    const devices = measurementDevices.externalDtos.filter((d) => activeCalc.measurementDeviceIds.includes(d.id ?? -1));
    const uniqueTsoIds = [...new Set(devices.map((d) => d.tsoId))];

    const allHierarchiesFetched = uniqueTsoIds.every((tsoId) => deviceHierarchyByTso[tsoId]);
    if (!allHierarchiesFetched) return;

    const mappedSelectedTsos = uniqueTsoIds.map((tsoId) => {
      const tsoData = tsos?.externalDtos?.find((t) => t.id === tsoId);
      return {
        id: tsoId,
        name: tsoData?.name ?? '',
        active: true,
        abbreviation: tsoData?.abbreviation ?? '',
      };
    });

    const deviceTechnicalNames = mapDevicesToTreeIds(deviceHierarchyByTso, activeCalc.measurementDeviceIds);

    const paramMap = new Map(measurementParameters?.externalDtos?.map((p) => [p.id, p.technicalName]));

    const selectedTechnicalNames =
      activeCalc.measurementParameterIds
        ?.map((id) => paramMap.get(id))
        .filter((name): name is string => Boolean(name)) ?? [];

    const selectedParameters: CorrelationState['activeCalculationParameters'] = {
      calculationName: activeCalc.name ?? '',
      activeTsos: mappedSelectedTsos,
      start: activeCalc.start,
      end: activeCalc.end,
      deviceTechnicalNames,
      parameterType: activeCalc.type ? 'multiple' : 'single',
      type: Number(activeCalc.type),
      selectedParameters: selectedTechnicalNames,
      threshold: `${activeCalc.significanceThreshold}`,
    };

    dispatch(setCalculationParameters(selectedParameters));
  }, [calculations?.externalDtos, deviceHierarchyByTso, dispatch, measurementDevices?.externalDtos, measurementParameters?.externalDtos, selectedCalculation, tsos?.externalDtos]);

  useEffect(() => {
    if (!selectedCalculation || !calculations?.externalDtos) return;

    const fetchCorrelationData = async () => {
      const fullCalculation = calculations.externalDtos?.find(
        (theCalculation) => theCalculation.id === selectedCalculation.id
      );
      if (!fullCalculation || fullCalculation.id === undefined) return;

      const id = fullCalculation.id;
      const type = fullCalculation.type;

      try {
        dispatch(setIsLoading(true));
        if (type === 0) {
          const result = await triggerGetSingleCoefficients(id, true).unwrap();
          dispatch(setSingleCoefficients(result));
          dispatch(setIsLoading(false));
        } else if (type === 1) {
          const result = await triggerGetCounts(id, true).unwrap();
          dispatch(setCorrelationCounts(result));
          dispatch(setIsLoading(false));
        }
      } catch (error) {
        console.error('Error fetching correlation data:', error);
      }
    };

    fetchCorrelationData();
  }, [calculations?.externalDtos, dispatch, selectedCalculation, triggerGetCounts, triggerGetSingleCoefficients]);

  return (
    <>
      <StyledSectionWrapper>
        <StyledMeasurementDeviceTitle>{t('correlation.submittedCalculations')}</StyledMeasurementDeviceTitle>
        <StyledLineWrapper>
          <SelectTsoPq
            options={options}
            label={t('correlation.correlationsResults')}
            value={selectedCalculation ?? null}
            handleChange={handleCalculationChange}
            customwidth="27.375rem"
            placeholder={t('correlation.chooseValue')}
            clearable
          />
          <PermissionGate roles={userOrSupportRestriction}>
            <StyledDeleteButton
              buttonType={selectedCalculation?.id ? 'outlinedPrimary' : 'outlinedNeutral'}
              sizeVariant="m"
              icon={faTrash}
              onClick={handleDeleteModal}
              disabled={!selectedCalculation?.id}
              tooltip={t('correlation.deleteDialogTitle')}
            />
          </PermissionGate>
        </StyledLineWrapper>
      </StyledSectionWrapper>{' '}
      {selectedCalculation && (
        <ModalDialogTsoPq
          title={t('correlation.deleteDialogTitle')}
          modalState={openModal}
          deleteConfirmationModal={true}
          confirmationText={deleteConfirmationMessage}
          onConfirm={() => handleCalculationDelete()}
          onCancel={() => setOpenModal(false)}
        />
      )}
    </>
  );
};

export default SubmittedCorellations;
